import { getRegionIdFromCollectionId } from "../constants/formOptions";
import { metadataFieldInfo } from "../constants/metadataFields";

// Converts the database fields to our local-application fields
// See "dbFieldNamesToAppFields" object for name mapping
// Additional formatting changes are applied (e.g. converting a formatted string to an array)
export const convertDbPropertiesToAppFields = (properties) => {
  const dbMetadataFields = Object.keys(properties).reduce((accum, key) => {
    const appFieldName = dbFieldNamesToAppFields[key];
    if (appFieldName != null) {
      accum[appFieldName] = properties[key];

      // if app field accepts multiple values (e.g. should be array type) convert string value to an array
      if (
        metadataFieldInfo[appFieldName] &&
        metadataFieldInfo[appFieldName].acceptsMultipleValues &&
        !Array.isArray(properties[key])
      ) {
        accum[appFieldName] =
          properties[key] && properties[key].length > 0
            ? properties[key].split(",")
            : [];
      }

      // Handle baselineModel - convert comma-separated string to array (like county/city)
      if (appFieldName === "baselineModel") {
        if (Array.isArray(properties[key])) {
          // Already an array from backend
          accum[appFieldName] = properties[key];
        } else if (properties[key] && typeof properties[key] === "string") {
          // Comma-separated string from DB, split into array
          accum[appFieldName] = properties[key]
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
        } else if (properties[key]) {
          // Single value, convert to array
          accum[appFieldName] = [properties[key]];
        } else {
          accum[appFieldName] = [];
        }
      }
    }
    return accum;
  }, {});
  // Sometimes the region is returned as "collection_id", other times as "collection"
  const regionId =
    getRegionIdFromCollectionId(properties.collection_id) ||
    getRegionIdFromCollectionId(properties.collection);
  return {
    tdisIdentifier: properties.tdisIdentifier,
    currentMetadata: {
      ...dbMetadataFields,
      twdbFloodPlanningRegionID: regionId,
    },
    statusInfo: {
      status: properties.status,
      timeOfLastStatusChange: properties.most_recent_status_update,
    },
  };
};

// Converts the application fields to database fields
// See "appFieldNamesToDBFieldNames" object for name mapping
// Additional formatting changes are applied (e.g. converting an array to a formatted string)
export const convertAppFieldsToDbProperties = (values, tdisIdentifier) => {
  const transformedValues = Object.keys(values).reduce((newObj, key) => {
    const dbKey = appFieldNamesToDBFieldNames[key];
    let transformedValue = values[key];

    // Handle baselineModel specially - convert array of objects/titles to comma separated strings
    if (dbKey === "baselineModel" && Array.isArray(transformedValue)) {
      // Convert objects to titles, then join as comma-separated string (like county/city)
      const titlesArray = transformedValue.map((item) => {
        if (item === "External Model") {
          return "External Model";
        }
        if (typeof item === "object" && item.title) {
          return item.title;
        }
        return item;
      });
      transformedValue = titlesArray.join(", ");
    } else if (Array.isArray(transformedValue)) {
      // For other arrays, convert to comma-separated string
      transformedValue = transformedValue.join(", ");
    }

    // Handle baselineModel (legacy single value) - if it's an object with title, extract just the title
    if (
      dbKey === "baselineModel" &&
      !Array.isArray(transformedValue) &&
      transformedValue &&
      typeof transformedValue === "object" &&
      transformedValue.title
    ) {
      transformedValue = transformedValue.title;
    }

    if (dbKey) {
      newObj[dbKey] = transformedValue;
    }
    return newObj;
  }, {});

  return {
    ...transformedValues,
    tdisIdentifier: tdisIdentifier,
  };
};

// Database field names to application field names
export const dbFieldNamesToAppFields = {
  region: "rbfsRegionID",
  huc: "hydrologicUnitCode",
  city: "city",
  county: "county",
  title: "modelName",
  description: "modelDescription",
  purpose: "modelPurpose",
  modelType: "modelType",
  modelCategory: "modelCategory",
  baselineModel: "baselineModel",
  keywordTerm: "kewyWordTerm",
  updateFrequency: "updateFrequency",
  contactAffiliation: "contactAffiliation",
  contactDepartment: "contactDepartment",
  contactEmail: "contactEmail",
  contactName: "contactName",
  contactPhoneNumber: "contactPhoneNumber",
  spatialDataType: "spatialDataType",
  spatialExtentDecimalDegrees: "spatialExtentDecimalDegrees",
  boundaryName: "boundaryName",
  horizontalCoordinateSystem: "horizantalCoordinateSystem",
  modelSoftwareName: "softwareName",
  modelSoftwareVersion: "softwareVersion",
  creator: "creator",
  modelInput: "modelInput",
  modelOutput: "modelOutput",
  modelConfigFile: "modelConfigFile",
  tdisIdentifier: "tdisIdentifier",
  id: "id",
  createdBy: "createdBy",
  createdDate: "createdDate",
  updatedBy: "updatedBy",
  updatedDate: "updatedDate",
  useConstraint: "useConstraint",
};

// Application field names to database field names
export const appFieldNamesToDBFieldNames = {
  rbfsRegionID: "region",
  hydrologicUnitCode: "huc",
  city: "city",
  county: "county",
  modelName: "title",
  modelDescription: "description",
  modelPurpose: "purpose",
  modelType: "modelType",
  modelCategory: "modelCategory",
  baselineModel: "baselineModel",
  kewyWordTerm: "keywordTerm",
  updateFrequency: "updateFrequency",
  contactAffiliation: "contactAffiliation",
  contactDepartment: "contactDepartment",
  contactEmail: "contactEmail",
  contactName: "contactName",
  contactPhoneNumber: "contactPhoneNumber",
  spatialDataType: "spatialDataType",
  spatialExtentDecimalDegrees: "spatialExtentDecimalDegrees",
  boundaryName: "boundaryName",
  horizantalCoordinateSystem: "horizontalCoordinateSystem",
  softwareName: "modelSoftwareName",
  softwareVersion: "modelSoftwareVersion",
  creator: "creator",
  modelInput: "modelInput",
  modelOutput: "modelOutput",
  modelConfigFile: "modelConfigFile",
  createdBy: "createdBy",
  createdDate: "createdDate",
  updatedBy: "updatedBy",
  updatedDate: "updatedDate",
  id: "id",
  tdisIdentifier: "tdisIdentifier",
  useConstraint: "useConstraint",
};
